import { useEffect, useState } from 'react';
import { usePlaidLink, PlaidLinkOptionsWithLinkToken } from 'react-plaid-link';

import { useQuery } from '@tanstack/react-query';

import { noop } from '@altirllc/shared/utils/functionalUtils';
import type { TActionP } from '@altirllc/shared/utils/types/common';

import { EVerificationMethod } from '@/config/payment';
import { CommonError, isResponseError } from '@/utils/errorUtils';

import type {
  TAddInstantMethodForm,
  TAddManualMethodForm,
  TAddPaymentMethodForm,
  TPaymentMethod,
  TPaymentMethodListItem,
} from '@/types/wallet';

import {
  useCreatePaymentMethod,
  useCreatePlaidLink,
  useInstantPaymentMethod,
} from '@/mutations/wallet';
import { paymentMethodsQuery } from '@/queries/wallet';

type TResolveInstant = (data: TAddInstantMethodForm) => Promise<TPaymentMethod>;
type TResolveManual = (data: TAddManualMethodForm) => Promise<TPaymentMethod>;

const tryRecognizeError = (error: unknown) => {
  if (isResponseError(error, 500, /^payment_processor_error_/)) {
    switch (error.response.data.errorCode) {
      case 'payment_processor_error_DuplicateResource':
        return new CommonError('stationSetup.connectBank.methodDuplicate');

      case 'payment_processor_error_MaxNumberOfResources':
        return new CommonError('stationSetup.connectBank.maxMethods');

      case 'payment_processor_error_ValidationError':
        return new CommonError('stationSetup.connectBank.invalidInfo');
    }
  }

  return error;
};

/**
 * Resolves the payment method the operator picked or adds a new one (Plaid bank login, or a bank
 * account typed in by hand). Used by the Add Payment Method modal and the create-station bank
 * step through `resolve(formValues)`.
 *
 * Data: the existing methods come from the saved query data (`paymentMethodsQuery`), shared with
 * the `PaymentMethod` component. Adding a method (W9 Plaid, W10 manual) goes through the wallet
 * save hooks, which cancel and mark ALL wallet data old, so every list refreshes by itself
 * (this replaces the old `invalidate(noArgs)`, which missed the `{ id: [...] }` list).
 * Errors: known payment-processor errors become a `CommonError` with a translated message; the
 * callers show it and log anything else.
 * Constraint: `resolve` for an existing method needs the list to be loaded; it rejects otherwise.
 */
export const usePaymentMethod = () => {
  const { data: methods } = useQuery(paymentMethodsQuery());
  const plaidLink = useCreatePlaidLink();
  const instantMethod = useInstantPaymentMethod();
  const manualMethod = useCreatePaymentMethod();

  const [plaidRequest, setPlaidRequest] = useState<PlaidLinkOptionsWithLinkToken>({
    token: null,
    onSuccess: noop,
  });

  // ToDo: Dig into the sources, get rid of the hook.
  //       All react wrapping is completely redundant here.
  const { open, ready } = usePlaidLink(plaidRequest);

  const resolveInstantVerification: TResolveInstant = async () => {
    const token = await plaidLink.mutateAsync();

    let resolve: TActionP<TPaymentMethodListItem> = noop;
    let reject: TActionP<unknown> = noop;

    setPlaidRequest({
      token,

      onSuccess: async (publicToken, meta) => {
        try {
          const [account] = meta.accounts;
          const method = await instantMethod.mutateAsync({
            publicToken,
            bankAccountId: account.id,
            bankAccountType: account.subtype,
            name: account.name,
            last4: account.mask,
          });

          resolve(method);
        } catch (err) {
          reject(err);
        }
      },

      onExit: (err) => {
        reject(err);
      },
    });

    return new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
  };

  const resolveManualVerification: TResolveManual = ({ bankAccount }) =>
    manualMethod.mutateAsync({
      country: 'US',
      currency: 'USD',
      ...bankAccount,
    });

  const resolve = async (data: TAddPaymentMethodForm) => {
    if (data.action === 'selectExisting') {
      const id = Number(data.data.paymentMethodId);
      const method = methods?.find((item) => item.id === id);

      if (!method) {
        throw new Error('The selected payment method is not in the loaded list');
      }

      return method;
    }

    try {
      switch (data.data.verification) {
        case EVerificationMethod.INSTANT:
          return await resolveInstantVerification(data.data);

        case EVerificationMethod.MANUAL:
          return await resolveManualVerification(data.data);
      }
    } catch (error) {
      throw tryRecognizeError(error);
    }
  };

  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  return { resolve };
};
